import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api2 from "~/api2"
import { UserProps } from "~/interfaces"
import {
  UserHookUpdateProps,
  UserHookInviteProps,
  UserHookDeleteProps,
  UserHookResendInviteProps,
} from "../interfaces/UserHookMethodsProps"

export const useUser = ({ camp_id }: { camp_id?: string }) => {
  const fetchUsers = useFetchUsers(camp_id)
  const updateUser = useUpdateUser(camp_id)
  const deleteUser = useDeleteUser(camp_id)
  const inviteUser = useInviteUser(camp_id)
  const resendInviteUser = useResendInviteUser(camp_id)

  const isLoading =
    fetchUsers.isPending ||
    updateUser.isPending ||
    inviteUser.isPending ||
    resendInviteUser.isPending

  const isError =
    fetchUsers.isError || updateUser.isError || inviteUser.isError || resendInviteUser.isError

  const error =
    fetchUsers.error || updateUser.error || inviteUser.error || resendInviteUser.error

  return {
    resource: fetchUsers.data,
    update: updateUser.mutate,
    destroy: deleteUser.mutate,
    inviteUser: inviteUser.mutate,
    resendInviteUser: resendInviteUser.mutate,
    isLoading,
    isError,
    error,
  }
}

const useFetchUsers = (camp_id: string) =>
  useQuery({
    queryKey: ["settingsUser", camp_id],
    queryFn: () => api2.Users.fetchAdminUsers(camp_id).then((res) => res.users),
  })

const useUpdateUser = (camp_id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      onSuccess,
      onError,
      selectedUserId,
      first_name,
      last_name,
      email,
      camp_ids,
      role,
      status,
    }: UserHookUpdateProps) => {
      try {
        return await api2.Users.updateAdminUser(selectedUserId, camp_id, {
          first_name,
          last_name,
          email,
          camp_ids,
          role,
          status,
        }).then((response) => {
          onSuccess()
          return response
        })
      } catch (error) {
        console.error("Error updating user:", error.message)
        onError(error)
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["settingsUser"] })
    },
  })
}

const useDeleteUser = (camp_id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ user_id, setUsers, onSuccess }: UserHookDeleteProps) => {
      try {
        return await api2.Users.deleteUser(user_id, camp_id).then(() => {
          setUsers((prev) => prev?.filter((user) => user.id !== user_id))
          onSuccess()
        })
      } catch (error) {
        console.error("Error deleting user:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settingsUser"] })
    },
  })
}

const useInviteUser = (camp_id: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      onSuccess,
      onError,
      first_name,
      last_name,
      email,
      camp_ids,
      role,
      status,
    }: UserHookInviteProps) => {
      try {
        return await api2.Users.createAdminUser(camp_id, {
          first_name,
          last_name,
          email,
          camp_ids,
          role,
          status,
        }).then((response) => {
          onSuccess()
          return response
        })
      } catch (error) {
        console.error("Error inviting user:", error.message)
        onError(error)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settingsUser"] })
    },
  })
}

const useResendInviteUser = (camp_id: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ onSuccess, selectedUserId }: UserHookResendInviteProps) => {
      try {
        return await api2.Users.resendInviteUser(selectedUserId, camp_id).then((response) => {
          onSuccess()
          return response
        })
      } catch (error) {
        console.error("Error inviting user:", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settingsUser"] })
    },
  })
}
